<script setup lang="ts">
import { cn } from "@/lib/utils";
import { eventBus } from "#bb/lib/eventBus";
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
import { buildAssessmentAnswerPayload } from "#bb/lib/assessmentAnswers";

// 1) Expect 'id' from the parent.
const props = defineProps<{ id: string }>();
const emit = defineEmits<{
  (e: "content-update", value: string | number): void;
  (e: "answer-error", payload: { id: string; error: unknown }): void;
}>();

// 2) Get the definition data from the content manager (read-only).
const { getContentById, getResolvedDataPath } = useContentManager();
const definitionData = computed(() => getContentById(props.id));

// 3) Inject features (e.g. editor mode).
const features = inject("render-features", { editor: false });

// 4) Define commands for the context menu.
const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "Building block editors",
    commands: [
      {
        id: "edit",
        name: "Edit Number Field",
        description: "Edit the number field",
        action: async () => {
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: definitionData.value,
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Number field",
    icon: "heroicons:pencil",
  },
});

// 5) Setup assessment answer binding (instance data only).
const { getAnswer, getCachedAnswer, setAnswer } = useAnswers();

const answer = ref<string | number>("");
const answerPath = computed(() => getResolvedDataPath(props.id).join("/"));

// Reactively mirror the shared answer cache so an answer made on any surface (the page or the AI
// chat) is reflected here live; getAnswer() ensures the cache has been loaded at least once.
watch(
  () => getCachedAnswer(props.id, answerPath.value)?.value,
  (value) => {
    const next = (value ?? "") as string | number;
    if (next !== answer.value) {
      answer.value = next;
    }
  },
  { immediate: true },
);

onMounted(() => {
  if (props.id) {
    void getAnswer(props.id, answerPath.value);
  }
});

// 6) Form-field validation wiring.
const { error, validate } = useFormField({
  id: props.id,
  componentId: ComponentDefinitionIds.NUMBER_INPUT,
  getValue: () => (answer.value === "" || answer.value === null || answer.value === undefined ? "" : Number(answer.value)),
  getValidation: () => definitionData.value?.validation,
  inputType: "number",
});

const save = async (newValue: string | number) => {
  answer.value = newValue;
  if (validate().ok === false || !props.id || !definitionData.value) {
    return;
  }

  try {
    await setAnswer(
      props.id,
      buildAssessmentAnswerPayload({
        block: definitionData.value,
        componentId: ComponentDefinitionIds.NUMBER_INPUT,
        value: answer.value,
        path: answerPath.value,
      }),
      { throwOnError: true },
    );
    emit("content-update", answer.value);
  } catch (error) {
    emit("answer-error", { id: props.id, error });
  }
};

const fieldId = useId();
</script>

<template>
  <div :class="cn('textbox prose max-w-none dark:prose-invert', $attrs.class ?? '')">
    <!-- Render either an Input or a Textarea based on the definition's 'multiline' flag -->
    <NumberField
      :id="fieldId"
      :model-value="answer"
      @update:model-value="save"
      @blur="validate"
    >
      <NumberFieldContent>
        <NumberFieldDecrement />
        <NumberFieldInput :aria-invalid="!!error" />
        <NumberFieldIncrement />
      </NumberFieldContent>
    </NumberField>
    <p v-if="error" class="text-destructive text-sm mt-1">{{ error }}</p>
  </div>
</template>

<script lang="ts">
import { ComponentDefinitionIds as ComponentDefinitionIdsMetadata } from "#bb/lib/componentDefinitionIds";
export default {
  componentId: ComponentDefinitionIdsMetadata.NUMBER_INPUT,
  icon: "lucide:text-cursor-input",
  type: "Number field",
  description: "A simple number input for the user to enter a number",
  default: {
    value: 0,
  },
  categories: ["Assessment"],
  order: 10005,
};
</script>
